import React, { useState, useRef, useCallback } from 'react'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import { useNavigate } from 'react-router-dom'
import { Box, Typography } from '@mui/material'
import { INTERAX_COLORS } from '../../../constants/colors'
import { useFeatureFlags } from '../../../shared/hooks/useFeatureFlags'
import { GamerRoutes } from '../../../config/routes/routeEnums'
import useFirebaseUser from '../../../hooks/useFirebaseUser'
import LanguageSwitcher from '../../../components/ui/LanguageSwitcher'
import useAppState from '../../../hooks/useAppState'
import { Button } from '../../../interaxDesignSystemComponents'
import { PageHeader } from '../../../shared/components/PageHeader'
import { EditAccountModal } from '../../../shared/components/EditAccountModal'
import { updateUser } from '../../../apiLayer'
import { updateProfile } from 'firebase/auth'
import { auth } from '../../../libs/Firebase'
import { User } from '../../../../../pure-js/types/User'
import { OnboardingChapter } from '../../../../../pure-js/enums/OnboardingChapter'
import { PageWrapper } from '../../../shared/components'
import { CommunicationPreferencesSection } from '../../../shared/communicationPreferences'
import { CoachPreferencesSection } from '../../../shared/coachPreferences/CoachPreferencesSection'
import { useIsCoachEnabled } from '../../../shared/hooks/useIsCoachEnabled'
import { MediaPermissionStatus } from '../../../shared/components/MediaPermissionStatus'
import { loadDevModeFromStorage, saveDevModeToStorage } from '../../../shared/libs/devModePersistence'
import { ReplayPlatformTourButton } from '../platformTour/components/ReplayPlatformTourButton'

/**
 * AccountSettingsPage for gamers
 * Shows user account information, language settings, and preferences
 */
export const AccountSettingsPage: React.FC = () => {
  const { t, i18n } = useTranslation()
  const isCoachEnabled = useIsCoachEnabled()
  const navigate = useNavigate()
  const {
    isGamerEnabled,
    isFacilitatorEnabled,
    isLanguageSwitcherEnabled,
    isLatestSessionFeedbackEnabled,
    isGenerateReportsEnabled,
    featureFlags
  } = useFeatureFlags()
  const { data: firebaseUser } = useFirebaseUser()
  const { state, setState } = useAppState()

  const totalOnboardingSteps = Object.keys(OnboardingChapter).length

  // Dev mode state - using shared persistence utility
  const [isDevMode, setIsDevMode] = useState(() => loadDevModeFromStorage())
  const [clickCount, setClickCount] = useState(0)
  const clickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Persist dev mode state to localStorage when it changes
  React.useEffect(() => {
    saveDevModeToStorage(isDevMode)
  }, [isDevMode])

  React.useEffect(
    () => () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current)
        clickTimeoutRef.current = null
      }
    },
    []
  )

  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editDisplayName, setEditDisplayName] = useState('')
  const [editPhoneNumber, setEditPhoneNumber] = useState('')
  const [isUpdating, setIsUpdating] = useState(false)
  const [updateError, setUpdateError] = useState('')

  // Get current language display name
  const getCurrentLanguageName = () => {
    switch (i18n.language) {
      case 'sv':
        return t('languages.swedish')
      case 'en':
      default:
        return t('languages.english')
    }
  }

  // Handle dev mode toggle (6 clicks on Account Information title)
  const handleAccountInfoClick = useCallback(() => {
    const newCount = clickCount + 1

    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current)
    }

    if (newCount >= 6) {
      setIsDevMode((prev) => !prev)
      setClickCount(0)
    } else {
      setClickCount(newCount)
      clickTimeoutRef.current = setTimeout(() => {
        setClickCount(0)
      }, 3000)
    }
  }, [clickCount])

  // Open edit modal with current values
  const handleEditClick = () => {
    setEditDisplayName(state.user?.displayName || firebaseUser?.displayName || '')
    setEditPhoneNumber(state.user?.phone || '')
    setUpdateError('')
    setIsEditModalOpen(true)
  }

  // Close edit modal
  const handleCloseModal = () => {
    setIsEditModalOpen(false)
    setUpdateError('')
  }

  // Save updated user information
  const handleSaveChanges = async () => {
    // Validate display name
    if (!editDisplayName.trim()) {
      setUpdateError(t('gamer.accountSettings.displayNameRequired'))
      return
    }

    setIsUpdating(true)
    setUpdateError('')

    try {
      const userId = state.user?.id || firebaseUser?.uid
      if (!userId) {
        throw new Error('No user ID found')
      }

      // Update user in database
      await updateUser(userId, {
        displayName: editDisplayName.trim(),
        phone: editPhoneNumber.trim() || undefined
      } as Partial<User>)

      // Update Firebase auth profile
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, {
          displayName: editDisplayName.trim()
        })
      }

      // Update local state
      setState({
        ...state,
        user: {
          ...state.user,
          displayName: editDisplayName.trim(),
          phone: editPhoneNumber.trim() || undefined
        }
      })

      setIsEditModalOpen(false)
    } catch (error) {
      setUpdateError(t('gamer.accountSettings.updateError'))
    } finally {
      setIsUpdating(false)
    }
  }

  return (
    <PageWrapper>
      {/* Page Header */}
      <PageHeader
        title={t('gamer.accountSettings.title')}
        subtitle={t('gamer.accountSettings.description')}
        actions={<ReplayPlatformTourButton />}
      />

      {/* Account Information */}
      {(firebaseUser || state.user) && (
        <Box
          sx={{
            padding: '24px',
            backgroundColor: INTERAX_COLORS.heritageBlue40,
            borderRadius: '12px',
            border: `1px solid ${INTERAX_COLORS.heritageBlue}`,
            marginBottom: '24px'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px'
            }}
          >
            <Typography
              variant="h5"
              onClick={handleAccountInfoClick}
              sx={{
                fontSize: '20px',
                fontWeight: 600,
                color: INTERAX_COLORS.white,
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              {t('gamer.accountSettings.accountInformation')}
              {isDevMode && (
                <Typography
                  component="span"
                  sx={{
                    marginLeft: '12px',
                    fontSize: '12px',
                    padding: '2px 8px',
                    backgroundColor: INTERAX_COLORS.sun,
                    color: INTERAX_COLORS.offBlack,
                    borderRadius: '4px',
                    fontWeight: 600
                  }}
                >
                  {t('gamer.accountSettings.devModeActive')}
                </Typography>
              )}
            </Typography>
            <Button variant="secondary" onClick={handleEditClick}>
              {t('gamer.accountSettings.editAccountInformation')}
            </Button>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {/* Avatar */}
            <Box
              sx={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${INTERAX_COLORS.heritageBlue}, ${INTERAX_COLORS.sun})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Typography
                sx={{
                  fontSize: '24px',
                  fontWeight: 600,
                  color: INTERAX_COLORS.white
                }}
              >
                {(state.user?.displayName || firebaseUser?.displayName)?.charAt(0) ||
                  (state.user?.email || firebaseUser?.email)?.charAt(0) ||
                  'G'}
              </Typography>
            </Box>

            {/* User Info */}
            <Box>
              <Typography
                sx={{
                  fontSize: '20px',
                  fontWeight: 600,
                  color: INTERAX_COLORS.white,
                  marginBottom: '4px'
                }}
              >
                {state.user?.displayName || firebaseUser?.displayName || t('gamer.profile.gamer')}
              </Typography>
              <Typography
                sx={{
                  fontSize: '16px',
                  color: INTERAX_COLORS.thunderGrey,
                  marginBottom: '4px'
                }}
              >
                {state.user?.email || firebaseUser?.email}
              </Typography>
              <Typography
                sx={{
                  fontSize: '14px',
                  color: INTERAX_COLORS.thunderGrey,
                  marginBottom: '4px'
                }}
              >
                {t('gamer.accountSettings.phoneNumber')}:{' '}
                {state.user?.phone || t('gamer.accountSettings.noPhoneNumber')}
              </Typography>
              <Typography
                sx={{
                  fontSize: '14px',
                  color: INTERAX_COLORS.thunderGrey
                }}
              >
                {t('gamer.accountSettings.userId')}: {state.user?.id || firebaseUser?.uid}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Media Permissions */}
      <Box
        sx={{
          padding: '24px',
          backgroundColor: INTERAX_COLORS.heritageBlue40,
          borderRadius: '12px',
          border: `1px solid ${INTERAX_COLORS.heritageBlue}`,
          marginBottom: '24px'
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontSize: '20px',
            fontWeight: 600,
            color: INTERAX_COLORS.white,
            marginBottom: '16px'
          }}
        >
          {t('gamer.accountSettings.mediaPermissions')}
        </Typography>
        <Typography
          sx={{
            fontSize: '14px',
            color: INTERAX_COLORS.thunderGrey,
            marginBottom: '16px'
          }}
        >
          {t('gamer.accountSettings.mediaPermissionsDescription')}
        </Typography>
        <MediaPermissionStatus />
      </Box>

      {/* Language Settings */}
      <Box
        sx={{
          padding: '24px',
          backgroundColor: INTERAX_COLORS.heritageBlue40,
          borderRadius: '12px',
          border: `1px solid ${INTERAX_COLORS.heritageBlue}`,
          marginBottom: '24px'
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontSize: '20px',
            fontWeight: 600,
            color: INTERAX_COLORS.white,
            marginBottom: '8px'
          }}
        >
          {t('gamer.accountSettings.language')}
        </Typography>
        <Typography
          sx={{
            fontSize: '14px',
            color: INTERAX_COLORS.thunderGrey,
            marginBottom: '16px'
          }}
        >
          {t('gamer.accountSettings.currentLanguage')}: {getCurrentLanguageName()}
        </Typography>
        <LanguageSwitcher />
      </Box>

      {/* AI Coach (Aria) contact preferences — only when the coach is enabled for the user's org */}
      {isCoachEnabled && <CoachPreferencesSection />}

      {/* Communication Preferences */}
      <CommunicationPreferencesSection />

      {/* Onboarding Progress */}
      <Box
        sx={{
          padding: '24px',
          backgroundColor: INTERAX_COLORS.heritageBlue40,
          borderRadius: '12px',
          border: `1px solid ${INTERAX_COLORS.heritageBlue}`
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontSize: '20px',
            fontWeight: 600,
            color: INTERAX_COLORS.white,
            marginBottom: '8px'
          }}
        >
          {t('gamer.accountSettings.onboardingProgress')}
        </Typography>
        <Typography
          sx={{
            fontSize: '14px',
            color: INTERAX_COLORS.thunderGrey,
            marginBottom: '12px'
          }}
        >
          {t('gamer.accountSettings.completedSteps')}: {state.user?.completedOnboardingSteps?.length || 0} /{' '}
          {totalOnboardingSteps}
        </Typography>
        {state.user?.completedOnboardingSteps && state.user.completedOnboardingSteps.length > 0 && (
          <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {state.user.completedOnboardingSteps.map((step) => (
              <Box
                key={step}
                sx={{
                  padding: '6px 12px',
                  backgroundColor: INTERAX_COLORS.green,
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: INTERAX_COLORS.white
                }}
              >
                ✓ {step}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Debug Tools Section - Only visible in dev mode */}
      {isDevMode && (
        <Box
          sx={{
            padding: '24px',
            backgroundColor: INTERAX_COLORS.heritageBlue40,
            borderRadius: '12px',
            border: `1px solid ${INTERAX_COLORS.heritageBlue}`,
            marginTop: '24px'
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontSize: '20px',
              fontWeight: 600,
              color: INTERAX_COLORS.white,
              marginBottom: '8px'
            }}
          >
            {t('gamer.accountSettings.debugTools')}
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              color: INTERAX_COLORS.thunderGrey,
              marginBottom: '16px'
            }}
          >
            {t('gamer.accountSettings.debugToolsDescription')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Debug Action Buttons */}
            <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <Button variant="primary" onClick={() => navigate('/gamer/onboarding')}>
                {t('gamer.accountSettings.testOnboarding')}
              </Button>
              <Button variant="secondary" onClick={() => navigate('/gamer/dev-tools')}>
                {t('gamer.accountSettings.devTools')}
              </Button>
              {featureFlags.enableExperimentalTeamFeedback && (
                <Box
                  component="button"
                  onClick={() => navigate(GamerRoutes.DEEPER_TEAM_ANALYSIS)}
                  sx={{
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontFamily: 'Lexend Deca',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: INTERAX_COLORS.white,
                    background: 'linear-gradient(135deg, rgba(255, 171, 64, 0.2) 0%, rgba(147, 51, 234, 0.2) 100%)',
                    border: '1px solid rgba(255, 171, 64, 0.5)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, rgba(255, 171, 64, 0.3) 0%, rgba(147, 51, 234, 0.3) 100%)'
                    }
                  }}
                >
                  ✨ View Experimental Feedback (Same URL)
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* Feature Flags Status - Only visible in dev mode */}
      {isDevMode && (
        <Box
          sx={{
            padding: '24px',
            backgroundColor: INTERAX_COLORS.heritageBlue40,
            borderRadius: '12px',
            border: `1px solid ${INTERAX_COLORS.heritageBlue}`,
            marginTop: '24px'
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontSize: '20px',
              fontWeight: 600,
              color: INTERAX_COLORS.white,
              marginBottom: '16px'
            }}
          >
            {t('gamer.accountSettings.featureFlags')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Gamer Feature Flag */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px',
                borderRadius: '8px',
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              }}
            >
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: 500,
                  color: INTERAX_COLORS.white
                }}
              >
                {t('gamer.accountSettings.gamerFeature')}
              </Typography>
              <Box
                sx={{
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: 600,
                  backgroundColor: isGamerEnabled ? INTERAX_COLORS.green : INTERAX_COLORS.thunderGrey,
                  color: INTERAX_COLORS.white
                }}
              >
                {isGamerEnabled ? t('gamer.accountSettings.enabled') : t('gamer.accountSettings.disabled')}
              </Box>
            </Box>

            {/* Facilitator Feature Flag */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px',
                borderRadius: '8px',
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              }}
            >
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: 500,
                  color: INTERAX_COLORS.white
                }}
              >
                {t('gamer.accountSettings.facilitatorFeature')}
              </Typography>
              <Box
                sx={{
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: 600,
                  backgroundColor: isFacilitatorEnabled ? INTERAX_COLORS.green : INTERAX_COLORS.thunderGrey,
                  color: INTERAX_COLORS.white
                }}
              >
                {isFacilitatorEnabled ? t('gamer.accountSettings.enabled') : t('gamer.accountSettings.disabled')}
              </Box>
            </Box>

            {/* Language Switcher Feature Flag */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px',
                borderRadius: '8px',
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              }}
            >
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: 500,
                  color: INTERAX_COLORS.white
                }}
              >
                {t('gamer.accountSettings.languageSwitcherFeature')}
              </Typography>
              <Box
                sx={{
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: 600,
                  backgroundColor: isLanguageSwitcherEnabled ? INTERAX_COLORS.green : INTERAX_COLORS.thunderGrey,
                  color: INTERAX_COLORS.white
                }}
              >
                {isLanguageSwitcherEnabled ? t('gamer.accountSettings.enabled') : t('gamer.accountSettings.disabled')}
              </Box>
            </Box>

            {/* Latest Session Feedback Feature Flag */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px',
                borderRadius: '8px',
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              }}
            >
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: 500,
                  color: INTERAX_COLORS.white
                }}
              >
                {t('gamer.accountSettings.latestSessionFeedbackFeature')}
              </Typography>
              <Box
                sx={{
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: 600,
                  backgroundColor: isLatestSessionFeedbackEnabled ? INTERAX_COLORS.green : INTERAX_COLORS.thunderGrey,
                  color: INTERAX_COLORS.white
                }}
              >
                {isLatestSessionFeedbackEnabled
                  ? t('gamer.accountSettings.enabled')
                  : t('gamer.accountSettings.disabled')}
              </Box>
            </Box>

            {/* Generate Reports Feature Flag */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px',
                borderRadius: '8px',
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              }}
            >
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: 500,
                  color: INTERAX_COLORS.white
                }}
              >
                {t('gamer.accountSettings.generateReportsFeature')}
              </Typography>
              <Box
                sx={{
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: 600,
                  backgroundColor: isGenerateReportsEnabled ? INTERAX_COLORS.green : INTERAX_COLORS.thunderGrey,
                  color: INTERAX_COLORS.white
                }}
              >
                {isGenerateReportsEnabled ? t('gamer.accountSettings.enabled') : t('gamer.accountSettings.disabled')}
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* Edit Account Information Modal */}
      <EditAccountModal
        isOpen={isEditModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveChanges}
        displayName={editDisplayName}
        phoneNumber={editPhoneNumber}
        onDisplayNameChange={setEditDisplayName}
        onPhoneNumberChange={setEditPhoneNumber}
        error={updateError}
        isUpdating={isUpdating}
        colors={{
          textPrimary: INTERAX_COLORS.white,
          textError: INTERAX_COLORS.redError
        }}
        translations={{
          title: t('gamer.accountSettings.editAccountInformation'),
          displayNameLabel: t('gamer.accountSettings.displayNameLabel'),
          displayNamePlaceholder: t('gamer.accountSettings.displayNamePlaceholder'),
          phoneNumberLabel: t('gamer.accountSettings.phoneNumberLabel'),
          phoneNumberPlaceholder: t('gamer.accountSettings.phoneNumberPlaceholder'),
          cancel: t('common.cancel'),
          save: t('common.save'),
          loading: t('common.loading')
        }}
      />
    </PageWrapper>
  )
}
